import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {
  DEFAULT_NOTIFICATION_TOTALS,
  DEFAULT_NOTIFICATIONS_CHIPS,
  INotification,
  INotificationChips,
  INotificationPerStore,
  INotificationTotals,
  IUserSelectedNotification,
} from '../models';
import {getDeepCopyOfObject, getShallowCopyOfObject} from '../../../shared/shared-utils/object/object.utils';
import {StockItemUpdateObj} from '../../../shared-utilities/models-old/datastructures';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IAutoOrderOrder} from '../../auto-ordering/models';
import {groupNotificationsPerUserStore, removeDuplicates, replaceNotificationInGrouped, replaceNotificationInList} from '../utils/notification.utils';
import {logConsoleError} from '../../../shared/shared-module-ui/utils/logger/logger';

export interface NotificationState {
  errors: IGlobalError[];
  filterChips: INotificationChips;
  isGetNotificationAutoOrderLoading: Record<string, boolean>;
  isGetNotificationStockUpdateLoading: Record<string, boolean>;
  isGetStockUpdateChangesLoading: boolean;
  isUpdateNotificationLoading: boolean;
  isUserNotificationsLoading: boolean;
  isUserScrollLoading: boolean;
  lastDocumentId: string;
  notificationAutoOrders: IAutoOrderOrder[];
  notificationPaginatedTotals: INotificationTotals;
  notificationStockUpdates: string;
  notificationUnreadFailedTotals: INotificationTotals;
  notificationUnreadTotals: INotificationTotals;
  notificationsPaginatedAll: INotification[];
  notificationsPaginatedGrouped: Record<string, INotificationPerStore>;
  notificationsUnreadAll: INotification[];
  notificationsUnreadFailedAll: INotification[];
  notificationsUnreadFailedGrouped: Record<string, INotificationPerStore>;
  notificationsUnreadGrouped: Record<string, INotificationPerStore>;
  retryExcludeFailedBarcodes: boolean;
  retryStockUpdate: boolean;
  selected: IUserSelectedNotification;
  stockViewChanges: Record<string, StockItemUpdateObj>;
}

export const initialNotificationsState: NotificationState = {
  errors: [],
  filterChips: getShallowCopyOfObject(DEFAULT_NOTIFICATIONS_CHIPS),
  notificationAutoOrders: [],
  isGetNotificationAutoOrderLoading: {},
  isGetNotificationStockUpdateLoading: {},
  isGetStockUpdateChangesLoading: false,
  isUpdateNotificationLoading: false,
  isUserNotificationsLoading: false,
  isUserScrollLoading: false,
  lastDocumentId: undefined,
  notificationStockUpdates: '',
  notificationPaginatedTotals: getShallowCopyOfObject(DEFAULT_NOTIFICATION_TOTALS),
  notificationsPaginatedAll: [],
  notificationsPaginatedGrouped: {},
  notificationUnreadTotals: getShallowCopyOfObject(DEFAULT_NOTIFICATION_TOTALS),
  notificationsUnreadAll: [],
  notificationsUnreadGrouped: {},
  notificationUnreadFailedTotals: getShallowCopyOfObject(DEFAULT_NOTIFICATION_TOTALS),
  notificationsUnreadFailedAll: [],
  notificationsUnreadFailedGrouped: {},
  retryStockUpdate: false,
  retryExcludeFailedBarcodes: false,
  selected: {
    autoOrder: undefined,
    notification: undefined,
    stockUpdate: undefined,
  },
  stockViewChanges: {},
};

const createNotificationReducer: ActionReducer<NotificationState> = createReducer(
  getDeepCopyOfObject(initialNotificationsState),
  //#region GLOBAL
  on(NotificationActions.setGlobalErrorOccurrence, (state: NotificationState, {error}) => {
    logConsoleError('Error Occurred:', error);
    return {
      ...state,
      errors: [...state.errors, error],
    };
  }),
  on(NotificationActions.setPropertyOnNotificationState, (state: NotificationState, {property, value}) => {
    return {
      ...state,
      [property]: value,
    };
  }),
  on(NotificationActions.setSelectedNotification, (state: NotificationState, {notification}) => {
    return {
      ...state,
      selected: {
        ...state.selected,
        notification,
      },
    };
  }),
  //#endregion
  //#region GET NOTIFICATIONS LIVE CHANGES
  on(NotificationActions.getAllNotificationsLiveChanges, (state: NotificationState) => {
    return {
      ...state,
      isUserNotificationsLoading: true,
      isUserScrollLoading: true,
    };
  }),
  on(NotificationActions.getAllNotificationsLiveChangesSuccess,
    (state: NotificationState, {notifications, storeIds}) => {
      const uniques = removeDuplicates(notifications);
      const notificationsGrouped = groupNotificationsPerUserStore(storeIds, uniques);
      return {
        ...state,
        notificationsPaginatedAll: uniques,
        isUserNotificationsLoading: false,
        isUserScrollLoading: false,
        lastDocumentId: notifications[notifications.length - 1]?.notificationId ?? undefined,
        notificationPaginatedTotals: notificationsGrouped.totals,
        notificationsPaginatedGrouped: notificationsGrouped.notificationsGrouped,
      };
    }),
  //#endregion
  //#region GET NOTIFICATIONS PAGES
  on(NotificationActions.getAllNotificationsPaged, (state: NotificationState) => {
    return {
      ...state,
      isUserNotificationsLoading: true,
      isUserScrollLoading: true,
    };
  }),
  on(NotificationActions.getAllNotificationsPagedSuccess,
    (state: NotificationState, {notifications, storeIds, lastDocumentId}) => {
      const combinedNotifications = [...state.notificationsPaginatedAll, ...notifications];
      const uniques = removeDuplicates(combinedNotifications);
      const notificationsGrouped = groupNotificationsPerUserStore(storeIds, uniques);
      return {
        ...state,
        notificationsPaginatedAll: uniques,
        isUserNotificationsLoading: false,
        isUserScrollLoading: false,
        lastDocumentId: lastDocumentId ?? undefined,
        notificationPaginatedTotals: notificationsGrouped.totals,
        notificationsPaginatedGrouped: notificationsGrouped.notificationsGrouped,
      };
    }),
  //#endregion
  //#region GET UNREAD NOTIFICATIONS
  on(NotificationActions.getAllUnreadNotifications, (state: NotificationState) => {
    return {
      ...state,
      isUserNotificationsLoading: true,
      isUserScrollLoading: true,
    };
  }),
  on(NotificationActions.getAllUnreadNotificationsSuccessful, (state: NotificationState, {
    storeIds,
    unreadNotifications,
  }) => {
    const notificationsGrouped = groupNotificationsPerUserStore(storeIds, unreadNotifications);
    return {
      ...state,
      isUserNotificationsLoading: false,
      isUserScrollLoading: false,
      notificationUnreadTotals: notificationsGrouped.totals,
      notificationsUnreadAll: unreadNotifications,
      notificationsUnreadGrouped: notificationsGrouped.notificationsGrouped,
    };
  }),
  //#endregion
  //#region GET UNSUCCESSFUL UNREAD NOTIFICATIONS
  on(NotificationActions.getAllUnsuccessfulUnreadNotifications, (state: NotificationState) => {
    return {
      ...state,
      isUserNotificationsLoading: true,
      isUserScrollLoading: true,
    };
  }),
  on(NotificationActions.getAllUnsuccessfulUnreadNotificationsSuccessful, (state: NotificationState, {
    storeIds,
    unreadNotifications,
  }) => {
    const notificationsGrouped = groupNotificationsPerUserStore(storeIds, unreadNotifications);
    return {
      ...state,
      isUserNotificationsLoading: false,
      isUserScrollLoading: false,
      notificationUnreadFailedTotals: notificationsGrouped.totals,
      notificationsUnreadFailedAll: unreadNotifications,
      notificationsUnreadFailedGrouped: notificationsGrouped.notificationsGrouped,
    };
  }),
  //#endregion
  //#region UPDATE NOTIFICATION
  on(NotificationActions.updateNotification, (state: NotificationState, {notification}) => {
    const patchList = (notifications: INotification[]): INotification[] =>
      replaceNotificationInList(notifications, notification);
    const selectedNotification: INotification | undefined = state.selected.notification;
    return {
      ...state,
      isUpdateNotificationLoading: true,
      notificationsPaginatedAll: patchList(state.notificationsPaginatedAll),
      notificationsUnreadAll: patchList(state.notificationsUnreadAll),
      notificationsUnreadFailedAll: patchList(state.notificationsUnreadFailedAll),
      notificationsPaginatedGrouped: replaceNotificationInGrouped(state.notificationsPaginatedGrouped, notification),
      notificationsUnreadGrouped: replaceNotificationInGrouped(state.notificationsUnreadGrouped, notification),
      notificationsUnreadFailedGrouped: replaceNotificationInGrouped(state.notificationsUnreadFailedGrouped, notification),
      selected: selectedNotification?.notificationId === notification.notificationId
        ? {
          ...state.selected,
          notification: {...selectedNotification, ...notification},
        }
        : state.selected,
    };
  }),
  on(NotificationActions.updateNotificationSuccess, (state: NotificationState) => {
    return {
      ...state,
      isUpdateNotificationLoading: false,
    };
  }),
  on(NotificationActions.batchUpdateNotifications, (state: NotificationState) => {
    return {
      ...state,
      isUpdateNotificationLoading: true,
    };
  }),
  on(NotificationActions.batchUpdateNotificationsSuccess, (state: NotificationState) => {
    return {
      ...state,
      isUpdateNotificationLoading: false,
    };
  }),
  //#endregion
  //#region GET NOTIFICATION AUTO ORDER
  on(NotificationActions.getNotificationAutoOrder, (state: NotificationState, {notification}) => {
    return {
      ...state,
      isGetNotificationAutoOrderLoading: {
        ...state.isGetNotificationAutoOrderLoading,
        [notification.event]: true,
      },
    };
  }),
  on(NotificationActions.getNotificationAutoOrderSuccess, (state: NotificationState, {autoOrder, notification}) => {
    const autoOrders = getDeepCopyOfObject(state.notificationAutoOrders);
    const index = autoOrders.findIndex((order: IAutoOrderOrder) => order.orderId === autoOrder.orderId);
    if (index > -1) {
      autoOrders[index] = autoOrder;
    } else {
      autoOrders.push(autoOrder);
    }
    return {
      ...state,
      notificationAutoOrders: autoOrders,
      isGetNotificationAutoOrderLoading: {
        ...state.isGetNotificationAutoOrderLoading,
        [notification.event]: false,
      },
      selected: {
        ...state.selected,
        stockUpdate: undefined,
        autoOrder: autoOrder,
      },
    };
  }),
  on(NotificationActions.getNotificationAutoOrderFailure, (state: NotificationState, {notification}) => {
    return {
      ...state,
      isGetNotificationAutoOrderLoading: {
        ...state.isGetNotificationAutoOrderLoading,
        [notification.event]: false,
      },
      selected: {
        ...state.selected,
        notification,
      },
    };
  }),
  //#endregion
  //#region GET NOTIFICATION STOCK UPDATE
  on(NotificationActions.getNotificationStockUpdate, (state: NotificationState, {notification}) => {
    return {
      ...state,
      isGetNotificationStockUpdateLoading: {
        ...state.isGetNotificationStockUpdateLoading,
        [notification.event]: true,
      },
    };
  }),
  on(NotificationActions.getNotificationStockUpdateSuccess, (state: NotificationState, {
    notification,
  }) => {
    return {
      ...state,
      isGetNotificationStockUpdateLoading: {
        ...state.isGetNotificationStockUpdateLoading,
        [notification.event]: false,
      },
    };
  }),
  on(NotificationActions.getStockUpdateChanges, state => ({
    ...state,
    isGetStockUpdateChangesLoading: true,
    stockViewChanges: {},
  })),
  on(NotificationActions.getStockUpdateChangesSuccess, (state, {stockViewChanges}) => ({
    ...state,
    stockViewChanges,
    isGetStockUpdateChangesLoading: false,
  })),
  on(NotificationActions.revertStockUpdate, (state) => ({
    ...state,
    isGetStockUpdateChangesLoading: true,
  })), on(NotificationActions.revertStockUpdateSuccess, (state) => ({
    ...state,
    isGetStockUpdateChangesLoading: false,
  })), on(NotificationActions.retryStockUpdateSuccess, (state) => ({
    ...state,
    isGetStockUpdateChangesLoading: false,
  })), on(NotificationActions.setRetryStockUpdate, (state, {retryStockUpdate, excludeFailedBarcodes}) => ({
    ...state,
    retryStockUpdate,
    retryExcludeFailedBarcodes: excludeFailedBarcodes ?? false,
  })),
  //#endregion
  //#region CLEAR NOTIFICATION
  on(NotificationActions.clearNotificationState, (state: NotificationState, {clearErrors, clearNotifications}) => {
    return {
      ...state,
      errors: clearErrors ? [] : state.errors,
      notificationAutoOrders: [],
      isGetNotificationAutoOrderLoading: {},
      isGetNotificationStockUpdateLoading: {},
      isUpdateNotificationLoading: false,
      isUserNotificationsLoading: false,
      notificationStockUpdates: '',
      notificationPaginatedTotals: clearNotifications ? {...DEFAULT_NOTIFICATION_TOTALS} : state.notificationPaginatedTotals,
      notificationsPaginatedAll: clearNotifications ? [] : state.notificationsPaginatedAll,
      notificationsPaginatedGrouped: clearNotifications ? {} : state.notificationsPaginatedGrouped,
      notificationUnreadTotals: clearNotifications ? {...DEFAULT_NOTIFICATION_TOTALS} : state.notificationUnreadTotals,
      notificationsUnreadAll: clearNotifications ? [] : state.notificationsUnreadAll,
      notificationsUnreadGrouped: clearNotifications ? {} : state.notificationsUnreadGrouped,
      notificationUnreadFailedTotals: clearNotifications ? {...DEFAULT_NOTIFICATION_TOTALS} : state.notificationUnreadFailedTotals,
      notificationsUnreadFailedAll: clearNotifications ? [] : state.notificationsUnreadFailedAll,
      notificationsUnreadFailedGrouped: clearNotifications ? {} : state.notificationsUnreadFailedGrouped,
      selected: {
        autoOrder: undefined,
        notification: undefined,
        stockUpdate: undefined,
      },
    };
  }),
  //#endregion
  //#region SET NOTIFICATION CHIPS
  on(NotificationActions.setNotificationFilterChips, (state: NotificationState, {filters}) => {
    return {
      ...state,
      filterChips: filters,
    };
  }),
  //#endregion
);

export const notificationReducer = (state: NotificationState, action: Action): NotificationState => createNotificationReducer(state, action);
